/**
 * The notification grid — plan §6.2.
 *
 * Four event types × two channels, set per contact-property link, falling back
 * to the contact's own defaults. Routing reads this grid and nothing else, so
 * "why did Rita get this and Dave not?" has one answer you can point at.
 */
export const NOTIFICATION_EVENTS = [
    'quotes',
    'visit_reminders',
    'job_updates',
    'invoices',
    /*
     * A salesperson's site visit, confirmed by New request (`docs/new-request.md`
     * §4.6, §12). Texts go only with consent recorded for that number.
     */
    'appointments',
];
/*
 * Post, then email, then text — the order they are drawn in and the order
 * `channelsFor` lists them. Widest reach first; it is also the order people
 * read the row of three glyphs above the tick boxes.
 */
export const NOTIFICATION_CHANNELS = ['mail', 'email', 'sms'];
/**
 * Which channels an event can actually go out on.
 *
 * Posting a quote takes four days and a quote is a conversation, so `mail` is
 * offered on invoices and receipts and nowhere else. This is not a UI detail:
 * routing reads the grid, and a `mail` tick on a visit reminder would be a
 * promise the system has no way to keep.
 */
export const CHANNELS_FOR_EVENT = {
    quotes: ['email', 'sms'],
    visit_reminders: ['email', 'sms'],
    job_updates: ['email', 'sms'],
    invoices: ['email', 'sms', 'mail'],
    appointments: ['email', 'sms'],
};
export const channelAllowed = (event, channel) => CHANNELS_FOR_EVENT[event].includes(channel);
/** What each channel is called where there is room to say it. */
export const NOTIFICATION_CHANNEL_LABELS = {
    email: 'Email',
    sms: 'Text message',
    mail: 'Posted',
};
/** The labels the profile mockups use, in the grid's row order. */
export const NOTIFICATION_EVENT_LABELS = {
    quotes: 'Quotes & approvals',
    visit_reminders: 'Visit reminders',
    job_updates: 'Job updates & photos',
    invoices: 'Invoices & receipts',
    appointments: 'Appointments',
};
export function emptyGrid() {
    return Object.fromEntries(NOTIFICATION_EVENTS.map((event) => [event, { email: false, sms: false, mail: false }]));
}
/**
 * Fills in anything a stored grid is missing, so a partial row cannot crash routing.
 *
 * `preferred` is the contact's Prefers, and decides one thing: the
 * Appointments row of a grid written before that row existed (0132) is on for
 * the channel the contact prefers (`docs/new-request.md` §4.6), rather than
 * off for everybody who was already a contact. Every other missing row is off.
 */
export function normalizeGrid(value, preferred) {
    const grid = emptyGrid();
    if (!value || typeof value !== 'object')
        return grid;
    const source = value;
    if (!('appointments' in source) && preferred) {
        grid.appointments.sms = preferred === 'sms' || preferred === 'either';
        grid.appointments.email = preferred === 'email' || preferred === 'either';
    }
    for (const event of NOTIFICATION_EVENTS) {
        const row = source[event];
        if (!row || typeof row !== 'object')
            continue;
        const channels = row;
        for (const channel of NOTIFICATION_CHANNELS) {
            // A channel this event cannot use is false whatever is stored. Grids
            // written before `mail` existed have no key for it, and a grid edited
            // by something other than the form must not be able to arrange for a
            // visit reminder to be posted.
            grid[event][channel] = channels[channel] === true && channelAllowed(event, channel);
        }
    }
    return grid;
}
/**
 * Which grid applies to a link.
 *
 * A link's own preferences win; `null` means it inherits, which is the
 * "Using defaults" state the contact profile draws. That distinction is why the
 * column is nullable — `{}` could not tell an inheriting link from one
 * deliberately switched all off.
 */
export function effectiveGrid(linkPrefs, contactDefaults, 
/** The contact's Prefers — see `normalizeGrid`. */
preferred) {
    if (linkPrefs === null || linkPrefs === undefined) {
        return { grid: normalizeGrid(contactDefaults, preferred), source: 'contact' };
    }
    return { grid: normalizeGrid(linkPrefs, preferred), source: 'link' };
}
export function channelsFor(grid, event) {
    return NOTIFICATION_CHANNELS.filter((channel) => grid[event][channel]);
}
/** True when this link gets nothing at all for an event. */
export const isSilentFor = (grid, event) => channelsFor(grid, event).length === 0;
//# sourceMappingURL=notifications.js.map